/**
 * Legendaries (PLAN §11): 2 per class, each changes an ability (never a plain +%), and the
 * item's socket gives 2× Gem Power. They drop from Champion up (`RankTierDef.legendaryChance`)
 * and only one can be equipped at a time (profile.equipItem enforces it), so the pair is a
 * build choice rather than a stack.
 */
import { ABILITY, CLASS } from '@keystone/content';
import type { ClassId } from '@keystone/sim';
import type { LegendaryDef } from './types';

const W = ABILITY.warden;
const L = ABILITY.lightbinder;
const B = ABILITY.blademaster;
const S = ABILITY.stormcaller;

/** Gem Power multiplier of a legendary's socket. */
export const LEGENDARY_SOCKET_MULT = 2;
/** Sockets on every legendary. */
export const LEGENDARY_SOCKETS = 1;
/** Equipped legendaries allowed at once. */
export const MAX_EQUIPPED_LEGENDARIES = 1;

export const LEGENDARIES: readonly LegendaryDef[] = [
  // ---- Warden ---------------------------------------------------------------
  {
    id: 'leg.warden.bastion',
    name: 'Bastion of the Line',
    classId: CLASS.warden,
    slot: 'chest',
    description: 'Last Stand has 2 charges.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [{ op: 'modifyCooldown', abilityId: W.lastStand, charges: 2 }],
  },
  {
    id: 'leg.warden.unbroken_aegis',
    name: 'Aegis of the Unbroken',
    classId: CLASS.warden,
    slot: 'weapon',
    description: 'Shield Slam cooldown is 2 s shorter and it heals you for 3% of your max HP.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [
      { op: 'modifyCooldown', abilityId: W.shieldSlam, deltaMs: -2000 },
      { op: 'addEffects', abilityId: W.shieldSlam, effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.03 } }] },
    ],
  },
  // ---- Lightbinder ------------------------------------------------------------
  {
    id: 'leg.lightbinder.dawnweaver',
    name: "Dawnweaver's Mantle",
    classId: CLASS.lightbinder,
    slot: 'helm',
    description: 'Flash Heal has a 1 s cast and also heals you for a small amount.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [
      { op: 'changeCast', abilityId: L.flashHeal, cast: { type: 'cast', timeMs: 1000 } },
      { op: 'addEffects', abilityId: L.flashHeal, effects: [{ type: 'heal', to: 'self', amount: { coef: 0.9 } }] },
    ],
  },
  {
    id: 'leg.lightbinder.radiant_chalice',
    name: 'Radiant Chalice',
    classId: CLASS.lightbinder,
    slot: 'ring',
    description: 'Radiant Wave cooldown is 10 s shorter and it can be cast while moving.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [
      { op: 'modifyCooldown', abilityId: L.radiantWave, deltaMs: -10000 },
      { op: 'patchAbility', abilityId: L.radiantWave, patch: { canCastWhileMoving: true } },
    ],
  },
  // ---- Blademaster --------------------------------------------------------------
  {
    id: 'leg.blademaster.stormwind_edges',
    name: 'Stormwind Edges',
    classId: CLASS.blademaster,
    slot: 'weapon',
    description: 'Bladestorm cooldown is 20 s shorter, it can be used while moving, and it hits 2 more targets.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [
      { op: 'modifyCooldown', abilityId: B.bladestorm, deltaMs: -20000 },
      { op: 'patchAbility', abilityId: B.bladestorm, patch: { canCastWhileMoving: true } },
      { op: 'modifyArea', abilityId: B.bladestorm, maxTargetsDelta: 2 },
    ],
  },
  {
    id: 'leg.blademaster.headsman',
    name: "Headsman's Grips",
    classId: CLASS.blademaster,
    slot: 'gloves',
    description: 'Execute can be used on targets below 35% health (was 20%).',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [{ op: 'patchAbility', abilityId: B.execute, patch: { usableIf: { type: 'targetHpBelow', pct: 0.35 } } }],
  },
  // ---- Stormcaller --------------------------------------------------------------
  {
    id: 'leg.stormcaller.thunderhead',
    name: 'Thunderhead Crown',
    classId: CLASS.stormcaller,
    slot: 'helm',
    description: 'Chain Lightning can be cast while moving and hits 2 more targets.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [
      { op: 'patchAbility', abilityId: S.chainLightning, patch: { canCastWhileMoving: true } },
      { op: 'modifyArea', abilityId: S.chainLightning, maxTargetsDelta: 2 },
    ],
  },
  {
    id: 'leg.stormcaller.magma_heart',
    name: 'Heart of the Magma',
    classId: CLASS.stormcaller,
    slot: 'chest',
    // Rotation rework: Lava Burst has 2 charges baseline, so the legendary grants the third.
    description: 'Lava Burst has 3 charges.',
    socketPowerMult: LEGENDARY_SOCKET_MULT,
    effect: [{ op: 'modifyCooldown', abilityId: S.lavaBurst, charges: 3 }],
  },
];

export function legendariesFor(classId: ClassId): LegendaryDef[] {
  return LEGENDARIES.filter((l) => l.classId === classId);
}
